import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack as BackIcon, CheckCircle as CheckIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../utils/api';
import LogEntryForm from './LogEntryForm';
import { LogTemplate } from './LogTemplateBuilder';
import { buildInitialResponsesFromEntry, ItemResponse, serializeResponsesForApi } from '../../utils/logEntryUtils';

interface AssignmentPayload {
  id: number;
  assignment_type: string;
  content_title: string;
  description: string;
  due_date: string | null;
  status: string;
  object_id: string;
}

interface CompletionPayload {
  id: number;
  status: string;
  completion_data: Record<string, unknown>;
}

interface LogEntryPayload {
  id: number;
  occurred_at: string;
  log_template_detail?: LogTemplate;
  responses?: Array<{ field: number; response_value?: string }>;
}

const LogPlayer: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [assignment, setAssignment] = useState<AssignmentPayload | null>(null);
  const [logTemplate, setLogTemplate] = useState<LogTemplate | null>(null);
  const [completion, setCompletion] = useState<CompletionPayload | null>(null);
  const [existingEntry, setExistingEntry] = useState<LogEntryPayload | null>(null);

  useEffect(() => {
    const load = async () => {
      if (!assignmentId) return;
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`/assignments/${assignmentId}/log-entry/`);
        setAssignment(response.data.assignment);
        setLogTemplate(response.data.log_template);
        setCompletion(response.data.completion);
        setExistingEntry(response.data.existing_entry);
      } catch (err: any) {
        setError(err.response?.data?.error || err.response?.data?.detail || 'Failed to load log assignment');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [assignmentId]);

  const isCompleted = completion?.status === 'completed';
  const readOnly = isCompleted || Boolean(existingEntry);

  const initialResponses = useMemo(
    () => (existingEntry ? buildInitialResponsesFromEntry(existingEntry as any) : undefined),
    [existingEntry],
  );

  const handleSubmit = async (data: { occurred_at: string; responses: Record<number, ItemResponse> }) => {
    if (!assignmentId) return;
    await api.post(`/assignments/${assignmentId}/log-entry/`, {
      occurred_at: data.occurred_at,
      status: 'submitted',
      responses: serializeResponsesForApi(data.responses),
    });
    setSuccess('Log submitted successfully.');
    setTimeout(() => navigate('/my-assignments'), 1500);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Button startIcon={<BackIcon />} onClick={() => navigate('/my-assignments')} sx={{ mb: 2 }}>
        Back to My Assignments
      </Button>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} icon={<CheckIcon />}>{success}</Alert>}

      <Paper sx={{ p: 3 }}>
        <Typography variant="h4" gutterBottom>
          {assignment?.content_title || logTemplate?.title || 'Log Assignment'}
        </Typography>
        {(assignment?.description || logTemplate?.description) && (
          <Typography variant="body1" color="text.secondary" paragraph>
            {assignment?.description || logTemplate?.description}
          </Typography>
        )}

        {isCompleted && (
          <Alert severity="success" sx={{ mb: 2 }}>
            This log assignment has been completed.
          </Alert>
        )}

        {logTemplate ? (
          <LogEntryForm
            template={logTemplate}
            initialOccurredAt={existingEntry?.occurred_at}
            initialResponses={initialResponses}
            readOnly={readOnly}
            submitLabel="Submit Log"
            onSubmit={readOnly ? undefined : handleSubmit}
          />
        ) : (
          <Alert severity="warning">Log template not found for this assignment.</Alert>
        )}
      </Paper>
    </Container>
  );
};

export default LogPlayer;
